import ApiRequest from '../api-request'
import { ICreateUserInput } from './create-user/create-user-input'
import { ICreateUserOutput } from './create-user/create-user-output'
import { IGetUsersOutput } from './get-users/get-users-output'
import { ILoginUserInput } from './login-user/login-user-input'
import { ILoginUserOutput } from './login-user/login-user-output'
import { ITwoFaLoginUserInput } from './two-fa-login-user/ITwoFaLoginUserInput'
import { ITwoFaLoginUserOutput } from './two-fa-login-user/ITwoFaLoginUserOutput'
import { IUpdateUserInput } from './update-user/update-user-input'
import { IUpdateUserOutput } from './update-user/update-user-output'
import { IUser } from 'app/models/user'
import { IForgotPasswordInput } from './forgot-password/forgot-password-input'
import { IConfigureTotpSecretInput } from './configure-totp-secret/configure-totp-secret-input'

export /** @type {*} */
  const UserRepository = {
    /**
     * @param {ILoginUserInput} input
     */
    loginUser: async (input: ILoginUserInput) => {
      const response = await ApiRequest<ILoginUserInput, ILoginUserOutput>({
        method: 'POST',
        path: '/auth/login',
        data: input,
        responseType: 'json',
      })

      if (response.error) {
        return {
          error: response.error.message,
          credentialsAreValid: false,
          statusCode: response.statusCode
        } as ILoginUserOutput
      }

      if (response.statusCode === 200) {
        const jwtToken = `${response.data?.token}`
        localStorage.setItem('token', jwtToken)

        return {
          credentialsAreValid: true,
          token: jwtToken,
          statusCode: response.statusCode
        } as ILoginUserOutput
      }

      return response.statusCode === 202
        ? ({ credentialsAreValid: true, needsTwoFa: true, isEmailTwoFa: response.data?.isEmailTwoFa ?? false } as ILoginUserOutput)
        : (response.data as ILoginUserOutput)
    },

    /**
     * @param {ITwoFaLoginUserInput} input
     */
    twoFaLoginUser: async (input: ITwoFaLoginUserInput) => await ApiRequest<ITwoFaLoginUserInput, ITwoFaLoginUserOutput>({
      method: 'POST',
      path: '/auth/twofalogin',
      data: input,
      responseType: 'json',
    }),

    /**
     *
     *
     * @param {({
     *     isGlobalAdmin: boolean | undefined,
     *     communityId: string | undefined,
     *     companyId: string | undefined
     *   })} [filters]
     * @return {*} 
     */
    getUsers: async (filters?: {
      isGlobalAdmin?: boolean,
      communityId?: string,
      companyId?: string
    }
    ) => await ApiRequest<null, IGetUsersOutput[]>({
      method: 'GET',
      path: '/users',
      responseType: 'json',
      params: filters
    })
    ,

    getUser: async (id: string) => ApiRequest<null, IUser>({
      method: 'GET',
      path: `/users/${id}`,
      responseType: 'json'
    }),

    /**
     * @creates the user
     */
    createUser: async (input: ICreateUserInput) =>
      ApiRequest<ICreateUserInput, ICreateUserOutput>({
        method: 'PUT',
        path: '/users',
        responseType: 'json',
        data: input,
      }),
    /**
     * @updates the user
     */
    updateUser: async (id: string, input: IUpdateUserInput) =>
      ApiRequest<IUpdateUserInput, IUpdateUserOutput>({
        method: 'POST',
        path: '/users/' + id,
        responseType: 'json',
        data: input,
      }),
    /**
     * deletes the user
     */
    deleteUser: async (id: string) =>
      ApiRequest<string, string>({
        method: 'DELETE',
        path: '/users/' + id,
        responseType: 'json',
      }),


    sendForgotPasswordEmail: async (email: string) => ApiRequest<string, string>({
      method: 'POST',
      path: '/users/sendforgotpassword',
      params: {
        email: email
      },
      responseType: 'json',
    }),

    forgotPassword: async (send: boolean, input: IForgotPasswordInput) => ApiRequest<IForgotPasswordInput, null>({
      method: 'POST',
      path: '/users/forgotpassword',
      params: send ? { action: 'send' } : {},
      responseType: 'json',
      data: input
    }),

    configureTotpSecret: async (id: string, secret: string, token: string) => ApiRequest<IConfigureTotpSecretInput, string>({
      method: 'POST',
      path: `/users/${id}/totp`,
      data: {
        secret,
        token
      },
      responseType: 'json',
    }),

    disableTotp2FA: async (id: string) => ApiRequest<null, null>({
      method: 'DELETE',
      path: `/users/${id}/totp`,
      responseType: 'json',
    })
  }
